<template>
  <div class="link-setting">
    <!-- 页面标题 -->
    <div class="page-header">
      <h2>购买链接设置</h2>
      <p class="page-description">管理代理后台的购买链接，支持添加多个购买链接</p>
    </div>

    <!-- 域名信息展示 -->
    <div class="domain-info" v-if="domain">
      <div class="domain-display">
        <div class="domain-text">
          <span class="domain-label">当前域名：</span>
          <span class="domain-value">{{ domain }}</span>
        </div>
        <el-button 
          type="text" 
          @click="handleCopyDomain" 
          icon="el-icon-copy-document"
          class="copy-btn"
        >
          复制
        </el-button>
      </div>
    </div>

    <!-- 操作栏 -->
    <div class="operation-bar">
      <el-button type="primary" @click="handleAdd" icon="el-icon-plus"
        >新增购买链接</el-button
      >
      <el-button
        type="success"
        @click="handleSave"
        icon="el-icon-check"
        :loading="saveLoading"
        >保存设置</el-button
      >
    </div>

    <!-- 购买链接列表 -->
    <div class="link-list">
      <el-card v-if="purchaseLinks.length === 0" class="empty-card">
        <div class="empty-content">
          <i class="el-icon-link"></i>
          <p>暂无购买链接，点击上方按钮添加</p>
        </div>
      </el-card>

      <el-card v-for="(link, index) in purchaseLinks" :key="index" class="link-card">
        <div class="link-item">
          <div class="link-info">
            <div class="link-name">
              <el-tag type="primary">{{ link.name }}</el-tag>
            </div>
            <div class="link-url">
              <el-input
                v-model="link.link"
                placeholder="请输入购买链接地址"
                :readonly="!link.editing"
                class="link-input"
              >
                <template slot="prepend">
                  <i class="el-icon-link"></i>
                </template>
              </el-input>
            </div>
          </div>
          <div class="link-actions">
            <el-button
              v-if="!link.editing"
              type="text"
              @click="handleEdit(index)"
              icon="el-icon-edit"
              >编辑</el-button
            >
            <el-button
              v-if="link.editing"
              type="text"
              @click="handleSaveEdit(index)"
              icon="el-icon-check"
              >保存</el-button
            >
            <el-button
              v-if="link.editing"
              type="text"
              @click="handleCancelEdit(index)"
              icon="el-icon-close"
              >取消</el-button
            >
            <el-button
              type="text"
              @click="handleCopy(link.link)"
              icon="el-icon-copy-document"
              >复制</el-button
            >
            <el-button
              type="text"
              @click="handleDelete(index)"
              icon="el-icon-delete"
              class="delete-btn"
              >删除</el-button
            >
          </div>
        </div>
      </el-card>
    </div>

    <!-- 新增/编辑对话框 -->
    <el-dialog
      :title="dialogTitle"
      :visible.sync="dialogVisible"
      width="500px"
      :before-close="handleDialogClose"
    >
      <el-form :model="formData" :rules="rules" ref="form" label-width="100px">
        <el-form-item label="链接名称" prop="name">
          <el-input
            v-model="formData.name"
            placeholder="请输入购买链接名称"
            maxlength="50"
            show-word-limit
          ></el-input>
        </el-form-item>
        <el-form-item label="链接地址" prop="link">
          <el-input v-model="formData.link" placeholder="请输入购买链接地址" type="url">
            <template slot="prepend">https://</template>
          </el-input>
        </el-form-item>
      </el-form>
      <div slot="footer" class="dialog-footer">
        <el-button @click="dialogVisible = false">取 消</el-button>
        <el-button type="primary" @click="submitForm">确 定</el-button>
      </div>
    </el-dialog>
  </div>
</template>

<script>
import { getMySettings, setMySettings } from "../api";
export default {
  name: "LinkSetting",
  data() {
    return {
      saveLoading: false,
      purchaseLinks: [],
      originalLinks: [],
      dialogVisible: false,
      dialogTitle: "新增购买链接",
      formData: {
        name: "",
        link: "",
      },
      rules: {
        name: [
          { required: true, message: "请输入链接名称", trigger: "blur" },
          { min: 2, max: 50, message: "长度在 2 到 50 个字符", trigger: "blur" },
        ],
        link: [
          { required: true, message: "请输入链接地址", trigger: "blur" },
          { type: "url", message: "请输入有效的链接地址", trigger: "blur" },
        ],
      },
      domain: ""
    };
  },
  created() {
    this.fetchData();
  },
  methods: {
    async fetchData() {
      try {
        const res = await getMySettings();
        const domains = res.data.domains || [];
        if(domains.length){
           this.purchaseLinks =  domains[0].purchase_link || [];
           this.domain = domains[0].domain;
        }
        this.backupOriginalData();
      } catch (error) {
        this.$message.error("获取购买链接失败");
      }
    },

    backupOriginalData() {
      this.originalLinks = JSON.parse(JSON.stringify(this.purchaseLinks));
    },

    handleAdd() {
      this.dialogTitle = "新增购买链接";
      this.formData = {
        name: "",
        link: "",
      };
      this.dialogVisible = true;
    },

    handleEdit(index) {
      this.$set(this.purchaseLinks[index], "editing", true);
      this.$set(
        this.purchaseLinks[index],
        "originalName",
        this.purchaseLinks[index].name
      );
      this.$set(
        this.purchaseLinks[index],
        "originalLink",
        this.purchaseLinks[index].link
      );
    },

    handleSaveEdit(index) {
      const link = this.purchaseLinks[index];
      if (!link.name || !link.link) {
        this.$message.warning("请填写完整的链接信息");
        return;
      }

      if (!this.isValidUrl(link.link)) {
        this.$message.warning("请输入有效的链接地址");
        return;
      }

      this.$set(this.purchaseLinks[index], "editing", false);
      this.$message.success("编辑成功");
    },

    handleCancelEdit(index) {
      const link = this.purchaseLinks[index];
      this.$set(this.purchaseLinks[index], "name", link.originalName);
      this.$set(this.purchaseLinks[index], "link", link.originalLink);
      this.$set(this.purchaseLinks[index], "editing", false);
    },

    async handleCopy(link) {
      try {
        await navigator.clipboard.writeText(link);
        this.$message.success("链接已复制到剪贴板");
      } catch (error) {
        const textArea = document.createElement("textarea");
        textArea.value = link;
        document.body.appendChild(textArea);
        textArea.select();
        document.execCommand("copy");
        document.body.removeChild(textArea);
        this.$message.success("链接已复制到剪贴板");
      }
    },

    async handleCopyDomain() {
      try {
        await navigator.clipboard.writeText(this.domain);
        this.$message.success("域名已复制到剪贴板");
      } catch (error) {
        const textArea = document.createElement("textarea");
        textArea.value = this.domain;
        document.body.appendChild(textArea);
        textArea.select();
        document.execCommand("copy");
        document.body.removeChild(textArea);
        this.$message.success("域名已复制到剪贴板");
      }
    },

    handleDelete(index) {
      this.$confirm("确认删除该购买链接？", "提示", {
        type: "warning",
      })
        .then(() => {
          this.purchaseLinks.splice(index, 1);
          this.$message.success("删除成功");
        })
        .catch(() => {});
    },

    async handleSave() {
      const editingItems = this.purchaseLinks.filter((item) => item.editing);
      if (editingItems.length > 0) {
        this.$message.warning("请先保存正在编辑的链接");
        return;
      }

      this.saveLoading = true;
      try {
        const saveData = {
          purchase_link: this.purchaseLinks.map((item) => ({
            name: item.name,
            link: item.link,
          })),
        };

        await setMySettings(saveData);
        console.log("保存的数据:", saveData);
        this.$message.success("保存成功");
        this.backupOriginalData();
      } catch (error) {
        this.$message.error("保存失败");
      } finally {
        this.saveLoading = false;
      }
    },

    submitForm() {
      this.$refs.form.validate((valid) => {
        if (valid) {
          this.purchaseLinks.push({
            name: this.formData.name,
            link: this.formData.link.startsWith("http")
              ? this.formData.link
              : "https://" + this.formData.link,
            editing: false,
          });

          this.dialogVisible = false;
          this.$message.success("添加成功");
        }
      });
    },

    handleDialogClose(done) {
      this.$refs.form.resetFields();
      done();
    },

    isValidUrl(string) {
      try {
        new URL(string.startsWith("http") ? string : "https://" + string);
        return true;
      } catch (_) {
        return false;
      }
    },
  },
};
</script>

<style lang="scss" scoped>
.link-setting {
  padding: 20px;

  .page-header {
    margin-bottom: 20px;

    h2 {
      margin: 0 0 8px 0;
      color: #303133;
      font-size: 24px;
      font-weight: 600;
    }

    .page-description {
      margin: 0;
      color: #909399;
      font-size: 14px;
    }
  }

  .domain-info {
    margin-bottom: 20px;

    .domain-display {
      display: flex;
      align-items: center;
      justify-content: space-between;
      padding: 12px 16px;
      background: #f8f9fa;
      border: 1px solid #e9ecef;
      border-radius: 6px;
      transition: all 0.3s ease;

      &:hover {
        border-color: #409eff;
        background: #f0f9ff;
      }

      .domain-text {
        display: flex;
        align-items: center;
        gap: 8px;
        font-size: 14px;

        .domain-label {
          color: #606266;
          font-weight: 500;
        }

        .domain-value {
          color: #409eff;
          font-weight: 600;
          font-family: 'Monaco', 'Menlo', 'Ubuntu Mono', monospace;
          background: #e1f3ff;
          padding: 4px 8px;
          border-radius: 4px;
          border: 1px solid #b3d8ff;
        }
      }

      .copy-btn {
        color: #606266;
        font-size: 12px;
        padding: 4px 8px;
        
        &:hover {
          color: #409eff;
          background: rgba(64, 158, 255, 0.1);
        }
      }
    }
  }

  .operation-bar {
    margin-bottom: 20px;

    .el-button {
      margin-right: 10px;
    }
  }

  .link-list {
    .empty-card {
      text-align: center;

      .empty-content {
        padding: 40px 0;

        i {
          font-size: 48px;
          color: #dcdfe6;
          margin-bottom: 16px;
        }

        p {
          margin: 0;
          color: #909399;
          font-size: 14px;
        }
      }
    }

    .link-card {
      margin-bottom: 16px;

      .link-item {
        display: flex;
        align-items: center;
        justify-content: space-between;

        .link-info {
          flex: 1;
          margin-right: 20px;

          .link-name {
            margin-bottom: 12px;

            .el-tag {
              font-size: 14px;
              font-weight: 500;
            }
          }

          .link-url {
            .link-input {
              :deep(.el-input__inner) {
                border: none;
                background: #f5f7fa;

                &:focus {
                  border: 1px solid #409eff;
                  background: #fff;
                }
              }

              &.is-readonly {
                :deep(.el-input__inner) {
                  background: transparent;
                  cursor: default;
                }
              }
            }
          }
        }

        .link-actions {
          display: flex;
          flex-direction: column;
          gap: 8px;

          .el-button {
            margin: 0;
            padding: 4px 8px;
            font-size: 12px;

            &.delete-btn {
              color: #f56c6c;

              &:hover {
                color: #f56c6c;
                background-color: #fef0f0;
              }
            }
          }
        }
      }
    }
  }

  :deep(.el-dialog) {
    .el-form-item__label {
      font-weight: 500;
    }
  }
}

@media (max-width: 768px) {
  .link-setting {
    padding: 16px;

    .domain-info {
      .domain-display {
        flex-direction: column;
        align-items: stretch;
        gap: 12px;

        .domain-text {
          justify-content: center;
        }

        .copy-btn {
          align-self: center;
        }
      }
    }

    .link-list {
      .link-card {
        .link-item {
          flex-direction: column;
          align-items: stretch;

          .link-info {
            margin-right: 0;
            margin-bottom: 16px;
          }

          .link-actions {
            flex-direction: row;
            justify-content: center;
            flex-wrap: wrap;
          }
        }
      }
    }
  }
}
</style>
